<template>
  <div class="button-container" :style="isNoBottomMargin">
    <button
      :disabled="disabled"
      @click="next"
      class="form-button"
      :class="{ 'form-button-160': staticWidth, 'form-button-disabled': disabled, 'dark': darkText && !disabled, 'white-button': whiteButton, }"
      :data-action="actionName"
    >
      <span v-if="!loading" key="next" v-translate>{{ buttonText }}</span>
      <span v-else key="spinner">
        <LoadSpinner v-if="!darkText" color="white"/>
        <LoadSpinner v-else color="black"/>
      </span>
    </button>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import SurveyResponse from '@/services/surveyresponses'
import LoadSpinner from '@/components/LoadSpinner'

export default {
  name: 'SubmitButton',
  props: {
    onContinue: {
      type: Function,
      default: () => {
        return true
      },
    },
    validate: {
      type: Function,
      default: () => {
        return true
      },
    },
    buttonText: {
      type: String,
      default: 'Next',
    },
    actionName: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    noNavigation: {
      type: Boolean,
      default: false,
    },
    state: {
      type: Object,
      default: () => {
        return {}
      },
    },
    staticWidth: {
      type: Boolean,
      default: false,
    },
    darkText: {
      type: Boolean,
      default: false,
    },
    actionName: {
      type: String,
      default: null,
    },
    whiteButton: {
      type: Boolean,
      default: false,
    },
    noBottomMargin: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    LoadSpinner,
  },
  data() {
    return {
      loading: false,
    }
  },
  created() {},
  destroyed() {},
  methods: {
    async next() {
      this.loading = true

      try {
        await this.validate()
        const continueWithIssue = await this.onContinue()
        if (this.noNavigation || continueWithIssue === false) {
          this.loading = false
          return
        }
      } catch(error) {
        this.loading = false
        throw error
      }

      var flow = { steps: [] }

      if (
        (this.currentRequest && this.currentRequest.isReproductiveRightsRequest) ||
        this.state['isReproductiveRightsRequest']
      ) {
        flow = this.reproductiveRightsFlow
      } else if (this.organizationDetails.isTcrOrganization) {
        // Determine which nominee flow to use based of the user's current program month
        let month = this.user.currentProgramMonth
        if (month == 1) {
          flow = this.nomineeMonthOneFlow
        } else if (month >= 2 && month <= 11) {
          flow = this.nomineeMonthsTwoThroughElevenFlow
        } else if (month == 12) {
          flow = this.nomineeMonthTwelveFlow
        } else {
          this.loading = false
          return
        }
      } else {
        flow = this.defaultRequestFlow
      }

      const nextRoute = flow.steps.find(step => step.show === this.$route.path).next
      if (typeof this[nextRoute] === 'function') {
        const routeString = await this[nextRoute]()

        this.$router.push(routeString)
      } else {
        this.$router.push(flow.steps.find(step => step.show === this.$route.path).next)
      }
    },
    async pickRouteFromEventCategories() {
      return '/create-events'
    },
    async pickRouteFromStart() {
      return '/pay-pal-verify'
    },
    async pickRouteFromEvents() {
      if (this.eventsRequireEventDocumentation) {
        return '/event-documentation'
      }

      return '/expense-categories'
    },
    async pickRouteFromEventDocumentation() {
      if (this.eventDocumentsMissing) {
        return '/event-warning'
      }
      else  {
        return '/expense-categories'
      }
    },
    async pickRouteFromExpenseDocumentation() {
      const householdDetailsSurveyComplete = await SurveyResponse.checkIfEmployeeHasCompleted(
        this.user,
        'Household Details',
      )
      const finHealthSurveyComplete = await SurveyResponse.checkIfEmployeeHasCompleted(
        this.user,
        'Financial Health',
      )

      if (!this.organizationDetails.expenseDocumentationEnabled) {
        if (this.organizationDetails.isHouseholdDetailsSurveyEnabled && !householdDetailsSurveyComplete) {
          return '/request-household-details'
        } else if (this.organizationDetails.isFinancialHealthSurveyEnabled && !finHealthSurveyComplete) {
          return '/request-financial-health'
        } else {
          return '/request-summary'
        }
      }

      if (this.expenseDocumentsMissing) {
        return '/expense-warning'
      } else {
        if (this.organizationDetails.isHouseholdDetailsSurveyEnabled && !householdDetailsSurveyComplete) {
          return '/request-household-details'
        } else if (this.organizationDetails.isFinancialHealthSurveyEnabled && !finHealthSurveyComplete) {
          return '/request-financial-health'
        } else {
          return '/request-summary'
        }
      }
    },
    async showExpenseWarningScreen() {
      if (
        this.currentRequest.amount <= 200 ||
        !this.organizationDetails.expenseDocumentationEnabled ||
        this.expenseSkipped
      )
        return false
      const empty = expense => {
        const has_expense_docs = expense.documentsRef.length > 0
        if (expense.categoryRef.name.toLowerCase().includes('food') && !has_expense_docs) {
          // 'Empty' if amount requested for food is > $200 and no documentation
          return expense.amount > 200
        }
        return !has_expense_docs
      }
      return this.currentRequest.expensesRef.some(empty)
    },
    async pickRouteFromExpenseCategories() {
      if (this.eventsRequireExpenseDocumentation) {
        return '/expense-documentation'
      } else {
        return '/request-summary'
      }
    },
  },
  computed: {
    ...mapState([
      'user',
      'currentRequest',
      'organizationDetails',
      'defaultRequestFlow',
      'reproductiveRightsFlow',

      // Nominee flows
      'nomineeMonthOneFlow',
      'nomineeMonthsTwoThroughElevenFlow',
      'nomineeMonthTwelveFlow',
    ]),
    selectedEvents() {
      return this.currentRequest.eventsRef
    },
    selectedExpenses() {
      return this.currentRequest.expensesRef
    },
    eventsRequireEventDocumentation() {
      return (
        this.organizationDetails.eventDocumentationEnabled &&
        this.selectedEvents.some(event =>
          event.categoryRef.requiredDocumentation &&
          event.categoryRef.requiredDocumentation.toLowerCase().includes("event")
        )
      )
    },
    eventsRequireExpenseDocumentation() {
      return (
        this.organizationDetails.expenseDocumentationEnabled &&
        this.selectedEvents.some(event =>
          event.categoryRef.requiredDocumentation &&
          event.categoryRef.requiredDocumentation.toLowerCase().includes("expense")
        )
      )
    },
    eventDocumentsMissing() {
      return (
        this.eventsRequireEventDocumentation &&
        this.selectedEvents.every(ev => ev.documentsRef.length === 0)
      )
    },
    expenseDocumentsMissing() {
      return (
        this.eventsRequireExpenseDocumentation &&
        this.selectedExpenses.every(ev => ev.documentsRef.length === 0)
      )
    },
    isNoBottomMargin() {
      if(!this.noBottomMargin) {
        return ""
      } else {
        return "margin-bottom: 0px;"
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/mixins/responsive';

.event-categories {
  display: flex;
  flex-direction: column;

  .event-categories-form {
    @extend .constrain-tablet-and-up;
  }
}
</style>
